import type { CookieConsentConfig } from "vanilla-cookieconsent";

import { reportConsent } from "./analytics";
import { notifyConsentChanged } from "./consent";

/**
 * Banner + preferences config for vanilla-cookieconsent.
 *
 * Replaced TermsFeed 2026-07-15. The old widget's stock copy told our visitors we
 * show "personalized content and targeted ads" (we show neither) and that tracking
 * cookies "do not identify any individual visitor" (ours do — `as_sid` is bound to
 * the userId on login). Consent given on false information isn't informed consent,
 * and its four hardcoded categories couldn't be reduced to the two we actually
 * have. Every string below is written against what this site really does.
 *
 * Only two categories, on purpose: we don't run advertising, and a toggle that
 * governs nothing is exactly the defect the 2026-07-15 audit was raised to fix.
 * Functional storage (volume, filters, dismissals) rides with `necessary` — it
 * exists only because the visitor chose it, which is the WP29 Opinion 04/2012
 * criterion-B "user-interface customisation" exemption.
 *
 * Design notes:
 *  - Accept and Reject are given EQUAL prominence (same level, same weight). An
 *    Accept that outshouts Reject is a dark pattern under EDPB Guidelines 03/2022
 *    and is the first thing an automated scanner or noyb complaint checks for.
 *  - `cookie.domain` MUST cover the API subdomain: the backend reads this cookie
 *    to decide whether to mint `as_sid`. The library defaults to location.hostname
 *    (host-only), which would never reach api.* and would leave the server half of
 *    the gate blind. Empty locally — cookies ignore the port.
 */

const COOKIE_DOMAIN = process.env.REACT_APP_COOKIE_DOMAIN || undefined;

/* The bottom bar used to be reserved for `/privacy` and `/imprint` — the two
   documents the banner links to — on the grounds that a centred box would cover
   the very text a reader had been sent to read. That reasoning was right, and it
   turned out to apply everywhere: the bar leaves the page legible and still
   holds the decision open. It is now the layout on every page, so the exception
   and the `ON_LINKED_LEGAL_PAGE` check it needed are both gone. */

export const cookieConsentConfig: CookieConsentConfig = {
  // Consent required before anything non-essential runs.
  autoShow: true,
  manageScriptTags: false, // our own gate handles script loading — see externalScriptsLoader
  disablePageInteraction: false,

  cookie: {
    name: "as_consent",
    domain: COOKIE_DOMAIN,
    path: "/",
    sameSite: "Lax",
    expiresAfterDays: 182, // ~6 months, then we ask again
  },

  guiOptions: {
    consentModal: {
      // A full-width bar at the bottom, on every page. It keeps the page behind
      // it readable while the decision is still pending, which is why the legal
      // documents used it first, and there is no page where covering the middle
      // of the screen served the reader better.
      layout: "bar",
      position: "bottom center",
      equalWeightButtons: true, // no dark pattern: accept/reject same weight
      flipButtons: false,
    },
    preferencesModal: {
      layout: "box",
      equalWeightButtons: true,
      flipButtons: false,
    },
  },

  // `onConsent` also fires on every page load, not just on a decision — the
  // backend dedupes on (consentId, categories), so re-reporting is harmless and
  // it means a consent given while the API was down still lands eventually.
  // Only `source` is sent; the server reads the decision itself off the cookie.
  // On the FIRST decision the library fires both of these, so reporting from
  // both sent two identical POSTs a millisecond apart and the server's
  // check-then-act dedupe lost the race — two identical rows at the same
  // instant. `onConsent` alone covers every case, including the first decision.
  // (Found by the consent E2E on 2026-07-28; the server is now atomic too, but
  // the honest fix is not to say the same thing twice.)
  onFirstConsent: () => {
    notifyConsentChanged();
  },
  onConsent: () => {
    reportConsent("banner");
    notifyConsentChanged();
  },
  onChange: () => {
    reportConsent("preferences");
    notifyConsentChanged();
  },

  categories: {
    necessary: {
      enabled: true,
      readOnly: true,
    },
    analytics: {
      enabled: false,
    },
  },

  language: {
    default: "en",
    translations: {
      en: {
        consentModal: {
          title: "Cookies on Audio Sanctum",
          description:
            "We use a few cookies to keep the site working — signing you in, remembering your cart. We'd also like to understand how the site is used, and to record anonymous sessions so we can find and fix problems. That part is entirely your choice, and the site works fine without it. We don't run ads and we don't sell your data.",
          acceptAllBtn: "Accept",
          acceptNecessaryBtn: "Decline",
          showPreferencesBtn: "Choose what to allow",
          // Both open in a NEW TAB, deliberately. A visitor must be able to
          // read the privacy information before consenting (Art 12/13 GDPR) and
          // to reach the Impressum without consenting first (§5 ECG) — but the
          // banner sits over the page, so navigating in place changed the
          // document underneath while the banner went on covering it. The link
          // was required and useless at the same time. Opening a tab leaves the
          // banner and the undecided choice exactly where they were.
          footer: `<a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a><a href="/imprint" target="_blank" rel="noopener">Imprint</a>`,
        },
        preferencesModal: {
          title: "Cookie settings",
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Decline optional",
          savePreferencesBtn: "Save my choices",
          closeIconLabel: "Close",
          sections: [
            {
              title: "What this is",
              description:
                "Cookies are small files stored on your device. Some things below aren't cookies but are stored in your browser in a similar way — we list those too, and mark them, because the rules cover both. Some are needed for the site to work at all; the rest are optional and off unless you turn them on. You can change your mind any time from the footer.",
            },
            {
              title: "Strictly necessary",
              description:
                "Required for the site to function, so these can't be switched off. They keep you signed in, remember what's in your cart, hold your player volume and browsing preferences, let you pay securely, protect our forms from abuse, and store this cookie choice itself. They are not used to build a profile of you or to advertise to you.",
              linkedCategory: "necessary",
              // The Type column exists because this list is NOT all cookies —
              // ePrivacy/TKG §165(3) covers anything stored on the device, so
              // localStorage belongs here too. Saying "cookie" about a
              // localStorage key would be a small lie in a document whose whole
              // job is being accurate.
              cookieTable: {
                headers: { name: "Name", type: "Type", desc: "Purpose", dur: "Duration" },
                body: [
                  { name: "as_refresh", type: "Cookie", desc: "Keeps you signed in.", dur: "30 days" },
                  { name: "as_consent", type: "Cookie", desc: "Remembers this cookie choice, so we don't ask again.", dur: "6 months" },
                  { name: "__stripe_mid", type: "Cookie (Stripe)", desc: "Set by Stripe, our payment provider, to detect and prevent payment fraud. Only if you open the cart, checkout or payment settings — Stripe is not loaded while you browse.", dur: "1 year" },
                  { name: "__stripe_sid", type: "Cookie (Stripe)", desc: "Set by Stripe to detect and prevent payment fraud during a checkout. Only set once you start a purchase.", dur: "30 minutes" },
                  { name: "m", type: "Cookie (Stripe)", desc: "Set by Stripe on m.stripe.com, also for payment-fraud detection. Only set once you start a purchase.", dur: "~13 months" },
                  { name: "accessToken / refreshToken", type: "Browser storage", desc: "Keeps you signed in between pages.", dur: "Until you sign out" },
                  { name: "as-cart", type: "Browser storage", desc: "Remembers what you added to your cart.", dur: "Until cleared" },
                  { name: "i18nextLng", type: "Browser storage", desc: "Remembers your language.", dur: "Until cleared" },
                  { name: "Player & browsing preferences", type: "Browser storage", desc: "Volume, catalog filters, and notices you've dismissed.", dur: "Until cleared" },
                ],
              },
            },
            {
              title: "Analytics and session recording",
              description:
                "Optional, and off unless you allow it. It lets us see which pages and searches lead somewhere, and records your session (OpenReplay) so we can find bugs and confusing steps. If you're signed in, this can be linked to your account. <strong>In the session recordings, whatever you type into a form is not captured at all, and email addresses shown on the page are masked.</strong> Decline and none of this runs — we'll still count page views and plays, but in a way that isn't tied to you or your device.",
              linkedCategory: "analytics",
              cookieTable: {
                headers: { name: "Name", type: "Type", desc: "Purpose", dur: "Duration" },
                body: [
                  { name: "as_sid", type: "Cookie", desc: "An anonymous id linking your visits, so we can count visitors rather than page loads. Linked to your account if you sign in.", dur: "1 year" },
                  { name: "__openreplay_uuid", type: "Browser storage (OpenReplay)", desc: "Identifies your device to the session recorder, so repeat visits can be recognised. Processed in the EU.", dur: "Until cleared" },
                  { name: "__openreplay_token", type: "Browser storage (OpenReplay)", desc: "Identifies the recording of this visit. Also __openreplay_token_version, __openreplay_tabid and __openreplay_pageno, which track which tab and page you're on within the recording.", dur: "Until you close the tab" },
                  { name: "as_attr", type: "Browser storage", desc: "Remembers where you arrived from (the referring site).", dur: "Until you close the tab" },
                  { name: "as_identified", type: "Browser storage", desc: "Notes that your visit has been linked to your account, so we only do it once.", dur: "Until you close the tab" },
                  { name: "as_active_search", type: "Browser storage", desc: "Remembers your current search, so we can tell which results were useful.", dur: "Until you close the tab" },
                ],
              },
            },
            {
              title: "More information",
              description: `The full list of what we store and why is in our <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>. If anything's unclear, <a href="/contact" target="_blank" rel="noopener">get in touch</a>.`,
            },
          ],
        },
      },
    },
  },
};
